import { useState, useEffect } from "react";
import API from "../api";

export default function WebsiteProjects() {

  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");

  const [form, setForm] = useState({
    id: null,
    name: "",
    client: "",
    progress: 0,
    status: "pending"
  });

  /* ===============================
     FETCH
  =============================== */
  const fetchProjects = async () => {
    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const res = await fetch(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json();
      setProjects(data.data || []);

    } catch (err) {
      console.error("❌ Fetch error:", err);
      setMessage("❌ Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  /* ===============================
     CREATE / UPDATE
  =============================== */
  const handleSubmit = async () => {
    try {
      if (!form.name || !form.client) {
        return setMessage("⚠️ Please fill all fields");
      }

      setSaving(true);

      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      const url = editMode
        ? `${API}/projects/${form.id}`
        : `${API}/projects`;

      const method = editMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...form,
          progress: Math.min(100, Math.max(0, form.progress))
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      setMessage(
        editMode ? "✅ Project updated" : "✅ Project created"
      );

      fetchProjects();
      closeModal();

    } catch (err) {
      console.error("❌ Save error:", err);
      setMessage("❌ Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  /* ===============================
     DELETE
  =============================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete project?")) return;

    try {
      const token =
        localStorage.getItem("token") ||
        sessionStorage.getItem("token");

      await fetch(`${API}/projects/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      setProjects(prev => prev.filter(p => p.id !== id));
      setMessage("🗑️ Project deleted");

    } catch (err) {
      console.error("❌ Delete error:", err);
      setMessage("❌ Failed to delete");
    }
  };

  /* ===============================
     EDIT
  =============================== */
  const handleEdit = (p) => {
    setForm(p);
    setEditMode(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setForm({
      id: null,
      name: "",
      client: "",
      progress: 0,
      status: "pending"
    });
  };

  /* ===============================
     FILTER
  =============================== */
  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  /* ===============================
     KPI (FIXED 🔥)
  =============================== */
  const normalize = (s) => (s || "").toLowerCase();

  const total = projects.length;
  const active = projects.filter(p => normalize(p.status) === "active").length;
  const completed = projects.filter(p => normalize(p.status) === "completed").length;
  const pending = projects.filter(p => normalize(p.status) === "pending").length;

  /* ===============================
     STATUS COLOR
  =============================== */
  const getStatusColor = (status) => {
    const s = normalize(status);

    if (s === "active") return "bg-green-100 text-green-700";
    if (s === "completed") return "bg-blue-100 text-blue-700";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div className="space-y-6">

      {/* MESSAGE */}
      {message && (
        <div className="p-3 bg-gray-100 rounded text-sm">
          {message}
        </div>
      )}

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Website Projects</h2>

        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg"
        >
          Add Project
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-white p-4 border rounded">Total: {total}</div>
        <div className="bg-white p-4 border rounded text-green-600">Active: {active}</div>
        <div className="bg-white p-4 border rounded text-blue-600">Completed: {completed}</div>
        <div className="bg-white p-4 border rounded text-yellow-600">Pending: {pending}</div>
      </div>

      {/* SEARCH */}
      <input
        className="w-full p-3 border rounded"
        placeholder="Search..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* TABLE (UPDATED 🔥) */}
      <div className="bg-white border rounded-xl overflow-x-auto">

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No projects found
          </div>
        ) : (
          <table className="w-full text-sm">

            {/* HEADER */}
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-4">Project</th>
                <th>Client</th>
                <th>Progress</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredProjects.map((p) => (
                <tr key={p.id} className="border-t hover:bg-gray-50">

                  <td className="p-4 font-medium">{p.name}</td>
                  <td>{p.client}</td>

                  {/* PROGRESS */}
                  <td className="w-48">
                    <div className="bg-gray-200 h-2 rounded">
                      <div
                        className="bg-green-600 h-2 rounded transition-all"
                        style={{ width: `${p.progress}%` }}
                      />
                    </div>
                    <small>{p.progress}%</small>
                  </td>

                  {/* STATUS */}
                  <td>
                    <span className={`px-2 py-1 text-xs rounded ${getStatusColor(p.status)}`}>
                      {p.status}
                    </span>
                  </td>

                  {/* ACTIONS */}
                  <td className="flex gap-2 p-4">
                    <button
                      onClick={() => handleEdit(p)}
                      className="text-blue-600"
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(p.id)}
                      className="text-red-600"
                    >
                      Delete
                    </button>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        )}

      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl w-96 space-y-3">

            <h3 className="font-bold">
              {editMode ? "Edit Project" : "Create Project"}
            </h3>

            <input
              placeholder="Name"
              className="w-full p-2 border rounded"
              value={form.name}
              onChange={(e)=>setForm({...form,name:e.target.value})}
            />

            <input
              placeholder="Client"
              className="w-full p-2 border rounded"
              value={form.client}
              onChange={(e)=>setForm({...form,client:e.target.value})}
            />

            <input
              type="number"
              className="w-full p-2 border rounded"
              value={form.progress}
              onChange={(e)=>setForm({...form,progress:e.target.value})}
            />

            <select
              className="w-full p-2 border rounded"
              value={form.status}
              onChange={(e)=>setForm({...form,status:e.target.value})}
            >
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
            </select>

            <div className="flex justify-end gap-2">
              <button onClick={closeModal}>Cancel</button>

              <button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                {saving ? "Saving..." : editMode ? "Update" : "Create"}
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}